/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import root from 'window-or-global';
import cx from 'classnames';
import { DealsModal } from '@/features/pdp/components/DealsModal';
import { ProductCarouselV2 } from '@/features/pdp/components/ProductCarousel/ProductCarouselV2';
import { Grid } from '../../Grid';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/Button';
import azCommonStyles from '../../../theme/globals.module.scss';
import { FulfillmentOptions as FulfillmentOptionsMX } from '../../FulfillmentOptions';
import { getFulfillmentOption } from '@/utils/getFulfillmentOption';
import { getMaxAvailQtyFromOptions } from '@/utils/itemHelpers/fulfillmentHelpers/getMaxAvailQtyFromOptions';
import { convertFulfillmentTypeToOrderType } from '@/utils/itemHelpers/lineItemHelpers/convertFulfillmentTypeToOrderType';
import { certonaPageType } from '@/constants/certonaPageType';
import { callToCertona } from '@/features/certona/api/callToCertona';
import { usePatchOrderItem } from '@/features/orders/api/patchOrderItem';
import type { VehicleSummary } from '@/types';
import { useAppData } from '@/hooks/useAppData';
import { Text, View } from '@az/starc-ui';
import { Skeleton } from '@az/starc-ui';
import LeftSectionSkeletonLoading from './SkeletonLoadingV2/LeftColumnSkeleton';
import type {
  ProductData,
  UseAddToCartWithMiniCartResult,
  AddToCartAnalyticsData,
} from '@/types/reduxStore/cartUpdate';
import FitmentButton from '../../AZCustomComponent/FitmentButton';
import { interpolateLabel } from '@/features/i18n/utils/interpolateLabel';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes, defaultStoreUS } from '@/constants/locale';
import { pageTypes } from '../../../constants/page';
import { itemDetail } from '../../../constants/images/itemDetail';
import { productInfoConstants } from '../../../constants/productInfoConstants';
import { cartConstants } from '../../../constants/cart';
import { routePaths } from '../../../constants/routePaths';
import { checkAvailabilityByGroupTypeId } from '@/features/orders/utils/checkAvailabilityByGroupTypeId';
import scrollToElem from '../../../utils/crossBrowserHelpers/scroll';
import getPosition from '../../../utils/crossBrowserHelpers/getPosition';
import {
  FULFILLMENT_METHODS,
  fulfillmentGroupTypeIds,
} from '../../../constants/fulfillmentConstants';
import CartButton, { type CartButtonRef } from '../../AZCustomComponent/CartButton';
import NextImage from '@/components/NextImage';
import { DealsDrawerButton } from '@/features/deals/components/DrawerOpenButton/DrawerOpenButton';
import { YMMEModal } from '@/features/ymme/components/YMMEModal';
import { getFormattedPriceMultiplied, getStaticFormattedPrice } from '../../../utils/priceHelpers';
import styles from './styles.module.scss';
import type { InteractionLocation } from '@/types/analytics';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { trackFulfillmentChange } from '@/utils/analytics/track/trackFulfillmentChange';
import { trackQuantityChange } from '@/utils/analytics/track/trackQuantityChange';
import { trackQuantityFulfillmentChange } from '@/utils/analytics/track/trackQuantityFulfillmentChange';
import { trackCustomizeSkuStart } from '@/utils/analytics/track/trackCustomizeSkuStart';
import { trackNearbyStoreUpdate } from '@/utils/analytics/track/trackNearbyStoreUpdate';
import { analyticsPDPReviewRating } from '../analytics/analyticsPDPReviewRating';
import { getProductStrikethroughAnalyticValue } from '../analytics/getProductStrikethroughAnalyticValue';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import WarrantyBadge from '../../AZCustomComponent/WarrantyBadge';
import { sessionStorage } from '../../../utils/sessionStorage';
import { useFeatureFlag } from '@/features/globalConfig';
import { useOMSFlags } from '@/features/product/utils/getOMSFlags';
import { useGCPPriceFlags } from '@/features/product/utils/getGCPPriceFlags';
import {
  getProductSkuDetailsKey,
  getProductSkuDetailsFromCache,
} from '@/features/product/api/getProductSkuDetails';
import { type ProductSkuDetails } from '@/features/product';
import ProductAvailabilityInfoSkeletonV2 from './ProductAvailabilityInfoSkeletonV2';
import { useQueryClient } from '@tanstack/react-query';
import type { ProductModel } from '@/api/types/products-types';
import { FulfillmentOptionButtonsV3 } from '@/components/FulfillmentOptionButtons/FulfillmentOptionButtonsV3';
import {
  getSelectedFulfilmentID,
  getSelectedFulfilmentForSelectedQty,
  getFulfillmentId,
  getSelectedFulfillmentInCart,
  getAllAvailableQuantitiesForSku,
} from '@/utils/getFulfillmentOption';
import { SisterStoreContext } from '@/features/fulfillment/context/SisterStoreContext';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import usePrevious from '@/utils/usePrevious';
import { ProductCrossReference } from '@/features/shelf/components/PageTemplates/Shelf/ResultsList/ProductCrossReference';
import PdpPriceQuantity from './PdpPriceQuantity';
import SeoContent from './SeoContent';
import PdpTitleNotesV2 from './PdpTitleNotes/PdpTitleNotesV2';
import PdpTechNotesV2 from './PdpTechNotes/PdpTechNotesV2';
import { clickTrack } from '@/utils/analytics/clickTrack';
import type { SkuDealsFromAPI } from '@/types/availability';
import { type OrderType } from '@/constants/orderTypes';
import { usePriceHiding } from '@/hooks/usePriceHiding';
import { usePreviouslyBoughtItem } from '@/hooks/usePreviouslyBoughtItem';
import { getXFusionQueryId } from '@/utils/xFusionQueryIdHelpers';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { setInteractionLocation } from '@/features/ymme/utils/ymmeLocationHelper';
import { type ShippingType } from '@/features/fulfillment';
import {
  determineIndexButtonToBeSelected,
  determineOptionsToDisplayButtons,
} from '@/features/fulfillment/api/optionsToDisplay';
import SuperSkuOptionSelector from './SuperSkus/SuperSkuOptionSelector';
import { useReviewStatistics } from '@/features/customReviews/api/getReviewStatistics';
import { ProductStatistics } from '@/features/customReviews/components/ProductStatistics/ProductStatistics';
import ACESFitmentButton from '@/components/AZCustomComponent/ACESFitmentButton';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import logger from '@/utils/logger';
import { getFormattedDisplayNotes } from '@/utils/getFormattedDisplayNotes';
import { PaypalPayLaterMessaging } from '@/features/braintree/components/PaypalPayLaterMessaging';
import { MaxQuantityNotificationBlockPdpV2 } from '@/components/AZCustomComponent/MaxQuantityNotificationBlockPdpV2';
import CalmWrapper from './PdpPriceQuantity/CalmWrapper';
import { ProductAvailabilityInfoBadge } from './ProductAvailabilityInfoBadge';
import { PdpSpecifications } from './PdpSpecifications';
import { PdpFeatures } from './PdpFeatures';
import { useProductSpecificationsExist } from '@/hooks/useProductSpecificationsExist';
import { MiddleColumnSkeleton } from './SkeletonLoadingV2/MiddleColumnSkeleton/MiddleColumnSkeleton';
import MiddleColumnSkeletonBottom from './SkeletonLoadingV2/MiddleColumnSkeleton/MiddleColumnSkeletonBottom';
import MiddleColumnSkeletonTop from './SkeletonLoadingV2/MiddleColumnSkeleton/MiddleColumnSkeletonTop';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { getProductRecommendationType } from '../analytics/getProductRecommendationType';
import { PurchasedDate } from '@/components/ActionCandidates/components/ProductCard/BuyAgainProduct/PurchasedDate';

const LazySearchStoreModal = dynamic(
  () =>
    import('@/components/Store/StoreModal/SearchStoreModal').then((mod) => mod.SearchStoreModal),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

type InitialProps = {
  changeFindProduct: () => void;
  showWarrantyModal: (a: string) => void;
  pdpAddToCartSuccess: boolean;
  addToCartButtonLoading: boolean;
  openConfigurableSku: () => void;
  preferredVehicle: VehicleSummary | undefined;
  hasVehicle: boolean;
  defaultFulfillmentOption: string;
  refetchProductData: () => Promise<any>;
  refetchSkuDetails: () => Promise<any>;
  productDetailsSuccess: boolean;
  skuDetailsDataLastUpdatedAt: any;
  setTrackQOHError: (a: boolean) => void;
  isBundleDealDrawerOpen: Function;
  count: number;
  setCount: Function;
  shippingType: ShippingType;
  setShippingType: Function;
  shippingTypeChanged: boolean;
  setShippingTypeChanged: React.Dispatch<React.SetStateAction<boolean>>;
  fulfillmentId: number;
  setFulfillmentId: (fulfillmentId: number) => void;
  setShouldOpenSpecsPanel: (shouldOpenSpecsPanel: boolean) => void;
  prevShippingType: string | undefined;
  shouldShowSkeleton: boolean;
  productDetailsUpdatedAt: number;
  setGroupId: (
    groupId: typeof FULFILLMENT_METHODS.STOREORDER | typeof FULFILLMENT_METHODS.ONLINEORDER,
  ) => void;
  groupId: typeof FULFILLMENT_METHODS.STOREORDER | typeof FULFILLMENT_METHODS.ONLINEORDER;
  quantityInCart: number;
  setQuantityInCart: (quantityInCart: number) => void;
  setShowBundleDeals: (showBundleDeals: boolean) => void;
  setShowYmmeDealsDrawer: (showYmmeDealsDrawer: boolean) => void;
  retailPrice: number | string;
  setRetailPrice: (retailPrice: number | string) => void;
  storeChanged: boolean;
  setStoreChanged: (storeChanged: boolean) => void;
};

const labelMap = {
  lblGoToCart: 'label_CategoryPage_body_GoToCart',
  lblAddToCart: 'button_ProductDisplayPage_body_AddToCart',
  lblAdd: 'label_MyAccountVehicle_maintenanceInterval_Add',
  lblToCart: 'label_ProductDisplayPage_body_ToCart',
  viewDealsItem: 'label_Deals_DealsPage_ViewSpecialDealsForThisItem',
  customizeYourFloorMats: 'label.configurableSku.customizeYourFloorMat',
  setVehicleFit: 'label.configurableSku.setVehicleFit',
  label_CategoryPage_body_Pinch2Fingers: 'label_CategoryPage_body_Pinch2Fingers',
  label_CategoryPage_body_ImageNotAvailable: 'label_CategoryPage_body_ImageNotAvailable',
  label_CategoryPage_body_SaveNow: 'label_CategoryPage_body_SaveNow',
  label_CategoryPage_body_PriceCut: 'label_CategoryPage_body_PriceCut',
  label_instant_saving: 'label_instant_saving',
  lblProductPartNumber: 'label_DetailsPage_body_PartNo',
  labelSeeProductPrice: 'label_See_Product_Price',
  label_update_cart: 'label_update_cart',
  lblSetVehicleToSeePricing: 'label_set_vehicle_to_see_pricing',
  label_check_nearby_stores: 'label_check_nearby_stores',
  label_for: 'label_checkout_shipping_For',
  labelUpdateCartToProductItems: 'label_update_cart_to_product_items',
  label_DetailsPage_body_Location: 'label_DetailsPage_body_Location',
};

let pdpCheckFits: InteractionLocation = 'Check if it fits';

type ProductAvailabilityInfoProps = InitialProps & {
  productDetails: ProductModel | undefined;
  skuDetailsData: ProductSkuDetails | null;
  skuDealsData: SkuDealsFromAPI[] | undefined;
  shouldShowSkeleton: boolean;
  autoFocus?: boolean;
  productData: ProductData;
  onAddToCartCall: UseAddToCartWithMiniCartResult['addToCartCall'];
  oemBrandName?: string;
  oemPartNumber?: string;
  xFusionQueryId?: string;
  staticPrice: number;
};

export default function ProductAvailabilityInfoV2({
  productDetails,
  skuDetailsData,
  skuDealsData,
  shouldShowSkeleton,
  autoFocus,
  onAddToCartCall,
  productData,
  fulfillmentId,
  oemBrandName,
  oemPartNumber,
  xFusionQueryId,
  staticPrice,
  ...props
}: ProductAvailabilityInfoProps) {
  /* VARIABLES */
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const locale = useLocale();
  const labels = useLabels(labelMap);
  const sisterStoreContext = useContext(SisterStoreContext);
  const { data: headerData } = useHeaderData();
  const catalogVehicleId = headerData?.vehicleMap?.catalogVehicleId ?? '';
  const { data: storeDetailsData } = useStoreDetailsData();
  const storeNumber = storeDetailsData?.storeNumber;
  const queryClient = useQueryClient();
  const { mutateAsync: patchOrderItem } = usePatchOrderItem();
  const { appData } = useAppData();
  const { isFetchingLocation } = appData;
  const isBopusNewMsgApplicable = useFeatureFlag('IS_BOPUS_MSG_AVAILABLE_US') === 'true';
  const checkoutNearbyStoresEnabled = useFeatureFlag('CHECKOUT_NEARBY_STORES_ENABLED') === 'true';
  const isDealBadgeEnabled = useForteDecisionFlagWithTrueDefault('dealBadgeEnabled');
  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const updateCartEnabled = useFeatureFlag('UPDATE_CART_ENABLED') === 'true';
  const isNewDealsApiEnabled = useFeatureFlag('IS_NEW_DEALS_API_ENABLED') === 'true';
  const { useOMSInventoryQohService } = useOMSFlags();
  const { useGCPPriceService } = useGCPPriceFlags();
  const isSuperSkusEnabled = useFeatureFlag('IS_SUPER_SKUS_ENABLED') === 'true';
  const isCustomReviewsEnabled = useFeatureFlag('CUSTOM_REVIEWS_ENABLED') === 'true';
  const hidePricing = usePriceHiding();
  const isDesktopXL = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isUS = locale === countryCodes.us;
  const { setPageSpecificDataAsync } = usePageSpecificData();
  const isBopusEnabled = storeDetailsData?.bopusEnabled;
  const isBopusMexicoStoreEnabled = locale === countryCodes.mx && isBopusEnabled;
  const getPreviouslyBoughtItem = usePreviouslyBoughtItem();
  const previouslyBoughtItem = getPreviouslyBoughtItem(productDetails?.itemId);
  const [productEdited, setProductEdited] = useState(false);
  const [prevGroupId, setPrevGroupId] = useState(0);
  const [prevGroupIdInCart, setPrevGroupIdInCart] = useState(0);
  const [pdpDealsModelOpen, setPdpDealsModelOpen] = useState(false);
  const [showYmme, setShowYmme] = useState(false);
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [displayQuantityWarningMessage, setDisplayQuantityWarningMessage] = useState(false);
  const [disableRemoveButton, setDisableRemoveButton] = useState(false);
  const [productAddedToCart, setProductAddedToCart] = useState(false);
  const [shouldShowFulfillmentOptions, setShouldShowFulfillmentOptions] = useState(false);

  const optionsThatAreDisplayed = determineOptionsToDisplayButtons(
    skuDetailsData?.fulfillmentOptions,
  );
  const selectedItemId =
    optionsThatAreDisplayed[determineIndexButtonToBeSelected(props.shippingType, fulfillmentId)]
      ?.fulfillmentTypeIdOriginal;
  const [selectedFulfillmentTypeIdOriginal, setSelectedFulfillmentTypeIdOriginal] =
    useState<number>(0);
  const cartButtonRef = useRef<CartButtonRef>(null);
  const seeDealsButtonRef = useRef<HTMLButtonElement>(null);
  const prevProductDetailsUpdate = usePrevious(props.productDetailsUpdatedAt);
  const prevSkuDetailsUpdate = usePrevious(props.skuDetailsDataLastUpdatedAt);
  const prevSkuId = usePrevious(productDetails?.itemId);
  const handleDealsModal = () => {
    setPdpDealsModelOpen(true);
  };
  const { acesEnabled } = useAcesFlags();
  const configurableSku = skuDetailsData?.skuPricingAndAvailability.configurableSku ?? false;
  const price = configurableSku
    ? skuDetailsData?.skuPricingAndAvailability.lowestPrice
    : skuDetailsData?.skuPricingAndAvailability.retailPrice;
  const shouldDisplaySuperSkus =
    isSuperSkusEnabled && !!productDetails?.availableOptions && !!productDetails?.choices;
  const skuId = skuDetailsData?.skuPricingAndAvailability.skuId ?? '';
  const { data: reviewStatistics, isPending: reviewsPending } = useReviewStatistics({
    skuNumbers: skuId ? [skuId] : [],
    enabled: !!skuId,
    select: (data: any) => data?.[0],
  });
  const partNumberDisplay = productDetails?.partNumber ?? '';
  const { productCompareDescriptions } = productDetails ?? {};
  const productCompareLocaleMap = locale.toUpperCase().replaceAll('-', '_');
  const pdpHasSpecifications = useProductSpecificationsExist();

  /* SHARED DATA - Hoisted to eliminate duplicates across helper functions */
  const skuPricingAndAvailability = skuDetailsData?.skuPricingAndAvailability;
  const quantityInCart = productData?.quantityInCart ?? 0;
  const addedToCart = quantityInCart > 0;
  const storeSelected = skuDetailsData?.storeSelected;
  const isMxWeb = locale === countryCodes.mx;
  const catalogRefIdNew = productDetails?.itemId ?? '';

  // Deal & rebate data (used in price display, badges, deal drawer)
  const dealAvailable = isNewDealsApiEnabled
    ? skuDealsData?.[0]?.skuDealsInfo?.dealAvailable
    : skuDetailsData?.aZBadgesFlagVO?.dealAvailable;
  const discountedPrice = isNewDealsApiEnabled
    ? skuDealsData?.[0]?.skuDealsInfo?.discountedPrice
    : skuPricingAndAvailability?.discountedPrice;
  const rebatesAvailable = isNewDealsApiEnabled
    ? (skuDealsData?.[0]?.skuRebatesInfo?.rebatesAvailable ?? false)
    : (skuPricingAndAvailability?.rebatesAvailable ?? false);
  const rebateUrl = isNewDealsApiEnabled
    ? skuDealsData?.[0]?.skuRebatesInfo?.rebateUrl
    : skuPricingAndAvailability?.rebateUrl;
  const rebateShortDesc = isNewDealsApiEnabled
    ? skuDealsData?.[0]?.skuRebatesInfo?.rebateShortDesc
    : skuPricingAndAvailability?.rebateShortDesc;

  // Fulfillment data (used in button logic, quantity stepper, availability checks)
  const selectedFulfillmentOption =
    skuPricingAndAvailability?.skuAvailabilityInfo.fulfillmentOptions?.find(
      (option) => option.fulfillmentTypeId === fulfillmentId,
    ) ||
    skuPricingAndAvailability?.skuAvailabilityInfo.fulfillmentOptions?.find(
      (option) => option.selected,
    );
  const isAvailable = selectedFulfillmentOption?.available;
  const sisterStoreAvailable =
    skuPricingAndAvailability?.skuAvailabilityInfo.fulfillmentOptions?.[0]?.sisterStoreAvailable ??
    false;

  // Custom SKU flags (used in button logic and conditional rendering)
  const customizeSKUs = acesEnabled
    ? productDetails?.configurableSkuFlag
    : skuPricingAndAvailability?.configurableSku;

  // Store data for sister store logic
  const storeData = skuPricingAndAvailability?.skuAvailabilityInfo?.fulfillmentOptions?.find(
    (item) => item.groupTypeId === props.groupId,
  );

  // Vehicle fitment badging information
  const { itemDescription, vehicleFitFlag, vehicleSpecificFlag, breadCrumbs, recordType } =
    productDetails ?? {};
  const vehicleFits = vehicleFitFlag ?? false;
  const vehicleSpecific = vehicleSpecificFlag ?? false;
  const hideLabel = !vehicleFits && !vehicleSpecific;
  const showFitmentBadge =
    (recordType === 'app' && !hideLabel) || productDetails?.vehicleFit === 'MAY_NOT_FIT';

  /* BEHAVIOR */
  const handleCloseModal = () => {
    setPdpDealsModelOpen(false);
  };

  const ratingView = () => {
    const ratingElement = document.getElementById('tab-reviews');

    if (ratingElement != null) {
      const client = getPosition(ratingElement);

      if (client.y !== 0) {
        scrollToElem(client.y - 75);
      }
    }
  };

  const currentSuperSkuSelection = useMemo(
    () =>
      Object.entries(productDetails?.availableOptions ?? {}).reduce(
        (acc, [key, options]) => {
          const selectedOption = options.find((option) => option.selected);
          const selectedValue = selectedOption?.value ?? '';
          return {
            ...acc,
            [key]: selectedValue,
          };
        },
        {} as Record<string, string>,
      ),
    [productDetails?.availableOptions],
  );

  const skuAvailableQuantities = useRef(
    getAllAvailableQuantitiesForSku(skuDetailsData?.fulfillmentOptions),
  );

  // Extract quantities for easier access // SHARED: extracted from ref for convenience
  const { sddQuantity, nddQuantity, sthQuantity } = skuAvailableQuantities.current;
  const availableQuantity = selectedFulfillmentOption?.availableQty ?? 0;

  const handleQuantityChange = useCallback(
    (
      quantity: number,
      quantityErrorMsg: boolean,
      showQuantityWarningMessage?: boolean,
      fulfillmentV2Id?: number,
    ) => {
      const {
        groupId,
        setCount,
        setRetailPrice,
        quantityInCart,
        productDetailsSuccess,
        pdpAddToCartSuccess,
      } = props;
      const storeData =
        skuDetailsData?.skuPricingAndAvailability?.skuAvailabilityInfo?.fulfillmentOptions?.find(
          ({ groupTypeId }) => {
            return groupTypeId === groupId;
          },
        );
      const shouldShowMessage =
        fulfillmentId === FULFILLMENT_METHODS.STOREORDER
          ? storeData && storeData?.availableQty > 0
          : fulfillmentId === FULFILLMENT_METHODS.ONLINEORDER
            ? sthQuantity > 0
            : true;

      if (shouldShowMessage) {
        setDisplayQuantityWarningMessage(showQuantityWarningMessage ?? false);
      }
      const newQuantity = quantity ? Math.abs(quantity) : 1;
      setCount(newQuantity);
      setRetailPrice(getFormattedPriceMultiplied(staticPrice, newQuantity));
      const fulfillmentGroupSelectedInCart = skuDetailsData?.fulfillmentOptions?.find(
        (option) => option.selected === true,
      );
      const fulfillmentSelectedInCart =
        fulfillmentGroupSelectedInCart?.groupFulfillmentOptions?.find(
          (option) => option.selected === true,
        );
      // Online order and Next day are the same button and this logic ensures it does not mark the two as different things
      const fulfillmentMethodChanged =
        Number(fulfillmentSelectedInCart?.fulfillmentTypeId) ==
          Number(fulfillmentV2Id ?? fulfillmentId) ||
        (Number(fulfillmentSelectedInCart?.fulfillmentTypeId) == 101 &&
          Number(fulfillmentV2Id ?? fulfillmentId) == 107) ||
        (Number(fulfillmentSelectedInCart?.fulfillmentTypeId) == 107 &&
          Number(fulfillmentV2Id ?? fulfillmentId) == 101);
      setProductEdited(
        quantityInCart > 0 &&
          !!shouldShowMessage &&
          (quantityInCart !== newQuantity || !fulfillmentMethodChanged) &&
          (productDetailsSuccess || pdpAddToCartSuccess),
      );
    },
    [
      fulfillmentId,
      props,
      skuDetailsData?.fulfillmentOptions,
      skuDetailsData?.skuPricingAndAvailability,
      staticPrice,
      sthQuantity,
    ],
  );

  const changeShippingTypeV2 = useCallback(
    (
      shippingType: string,
      groupId: number,
      maxAvailableQty: number,
      isSisterStoreAvailable?: boolean,
    ) => {
      const { setShippingType, setGroupId, quantityInCart, count } = props;
      setPrevGroupId(props.groupId);
      setShippingType(shippingType);
      setGroupId(groupId);

      const fulfillmentSelectedInCart = skuDetailsData?.fulfillmentOptions?.find(
        (option) => option.selected === true,
      );

      if (
        quantityInCart > 0 &&
        count === quantityInCart &&
        fulfillmentSelectedInCart?.groupId !== groupId
      ) {
        setProductEdited(true);
      } else if (
        quantityInCart > 0 &&
        count === quantityInCart &&
        fulfillmentSelectedInCart?.groupId === groupId
      ) {
        setProductEdited(false);
      } else {
        setProductEdited(quantityInCart > 0 || productAddedToCart);
      }
      if (isSisterStoreAvailable) {
        handleQuantityChange(1, false);
      } else if (count > maxAvailableQty && maxAvailableQty > 0) {
        handleQuantityChange(maxAvailableQty, false);
      }
    },
    [handleQuantityChange, productAddedToCart, props, skuDetailsData],
  );

  const changeShippingTypeV3 = (
    shippingType: typeof cartConstants.STOREORDER | typeof cartConstants.ONLINEORDER,
    groupId: typeof FULFILLMENT_METHODS.STOREORDER | typeof FULFILLMENT_METHODS.ONLINEORDER,
    maxAvailableQty: number,
    isSisterStoreAvailable?: boolean,
    fulfillmentTypeId?: number,
    fulfillmentTypeIdOriginal?: number,
  ) => {
    setSelectedFulfillmentTypeIdOriginal(Number(fulfillmentTypeIdOriginal));
    const { quantityInCart, count } = props;
    setPrevGroupId(props.groupId);
    setDisplayQuantityWarningMessage(false);
    props.setShippingTypeChanged(true);

    const selectedFulfillmentId = getSelectedFulfilmentID(
      skuDetailsData?.skuPricingAndAvailability.skuAvailabilityInfo.fulfillmentOptions ?? [],
    );

    props.setShippingType(shippingType);
    props.setGroupId(groupId);
    if (fulfillmentTypeId) {
      handleFulfillmentIdUpdate(fulfillmentTypeId);
    }
    if (
      quantityInCart > 0 &&
      count === quantityInCart &&
      fulfillmentTypeId === FULFILLMENT_METHODS.NEXTDAY &&
      selectedFulfillmentId === FULFILLMENT_METHODS.ONLINEORDER
    ) {
      setProductEdited(false);
    } else if (
      quantityInCart > 0 &&
      count === quantityInCart &&
      selectedFulfillmentId !== fulfillmentTypeId
    ) {
      setProductEdited(true);
    } else if (
      quantityInCart > 0 &&
      count === quantityInCart &&
      selectedFulfillmentId === fulfillmentTypeId
    ) {
      setProductEdited(false);
    } else {
      setProductEdited(quantityInCart > 0 || productAddedToCart);
    }
    if (isSisterStoreAvailable) {
      handleQuantityChange(1, false);
    } else if (count > maxAvailableQty && maxAvailableQty > 0) {
      handleQuantityChange(maxAvailableQty, false);
    }
  };

  const handleShowReviewAnalytics = useCallback(() => {
    const { pageName = '', pageType = '', siteSection = '' } = root.utag_data || {};
    const { averageOverallRating, totalReviewCount } = reviewStatistics || {};

    const analyticsData = {
      eventType: 'reviewsLinkInteractionPDP',
      pageName,
      pageType,
      siteSection,
      customLinkName: `reviewsInteraction - ${pageType}`,
      productBrandList: [productDetails?.subBrandName ?? ''],
      productIdList: [skuDetailsData?.skuPricingAndAvailability.skuId ?? ''],
      productPartNumberList: [productDetails?.partNumber ?? ''],
      productAverageRating: averageOverallRating ? Number(averageOverallRating) : '',
      productReviewCount: totalReviewCount ? Number(totalReviewCount) : '',
      ...(acesEnabled
        ? {
            partGroupId: [productDetails?.partGroupId ?? ''],
            partTerminologyId: [productDetails?.ecommProductId ?? ''],
          }
        : {
            originalPartTypeIdList: [productDetails?.ecommProductId ?? ''],
            productRepositoryIdList: [productDetails?.ecommProductId ?? ''],
          }),
    };
    clickTrack(analyticsData);
  }, [productDetails, skuDetailsData, reviewStatistics, acesEnabled]);

  const updateCart = useCallback(
    async (count: number = props.count) => {
      const prevcartCount =
        (skuDetailsData?.quantityInCart ?? 0) > 0 ? skuDetailsData?.quantityInCart : 1;
      const productIdFromProductDetails =
        (acesEnabled ? productDetails?.partGroupId : productDetails?.ecommProductId) ?? '';
      const onSuccess = () => {
        const skuDetailsQueryOptions = {
          skuIds: [productDetails?.itemId ?? null],
          vehicleId: catalogVehicleId,
          storeNumber: storeNumber,
          isNewDealsApiEnabled,
          useOMSInventoryQohService,
          useGCPPriceService,
        };
        const skuDetails = getProductSkuDetailsFromCache(queryClient, skuDetailsQueryOptions);
        const updatedSkuDetails = skuDetails?.map(
          (skuDetailItem: {
            skuPricingAndAvailability: { productId: string; skuId: string | null };
          }) => {
            if (skuDetailItem.skuPricingAndAvailability?.skuId === productDetails?.itemId) {
              const newArr = skuDetailsData?.fulfillmentOptions?.map((option) => {
                if (option.groupId === props.groupId) {
                  return { ...option, selected: true };
                } else {
                  return { ...option, selected: false };
                }
              });
              return {
                ...skuDetailItem,
                quantityInCart: count,
                fulfillmentOptions: newArr,
                itemPresentInCart: true,
              };
            }
            return skuDetailItem;
          },
        );
        const skuDetailsKey = getProductSkuDetailsKey(skuDetailsQueryOptions);
        queryClient.setQueryData(skuDetailsKey, updatedSkuDetails);
        cartButtonRef.current?.onSuccessfulAdd?.(true);
      };

      setDisableRemoveButton(true);

      const addToCartPayload = {
        skuId: productDetails?.itemId ?? '',
        productId: productIdFromProductDetails,
        quantity: count.toString(),
        fulfillmentTypeId: fulfillmentId,
        vehicleId: catalogVehicleId,
        storeNumber,
        xFusionQueryId,
      };
      await patchOrderItem(addToCartPayload);

      onSuccess();
      if (sessionStorage.getItem('sister_store_update_type') === 'select_store_and_update_cart') {
        const productType = root.sessionStorage?.getItem('productType') ?? null;
        trackNearbyStoreUpdate(
          'Update_Quantity',
          'In-Stock Near You: Set Store and Add to Cart: Quantity Change',
          {
            quantityInCart: props.quantityInCart,
            brandName: productDetails?.subBrandName,
            corePrice: price,
            shippingType: props.shippingType,
            skuNumber: productDetails?.itemId,
            partNumber: productDetails?.partNumber ?? '',
            productPrice: price,
            requestQuantity: props.quantityInCart,
            productIdList: [skuDetailsData?.skuPricingAndAvailability.skuId ?? ''],
            productPartList: [productDetails?.partTypeName ?? ''],
            storeId: storeNumber,
            storeSearchTerm: storeNumber,
            productRecommendationTypeList: productType ? 'Sponsored Product' : 'AZ Product',
            ...(acesEnabled
              ? {
                  partGroupId: [productDetails?.partGroupId ?? ''],
                  partTerminologyId: [productDetails?.ecommProductId ?? ''],
                }
              : {
                  originalPartTypeId: productDetails?.originalPartTypeId,
                  productRepositoryId: productDetails?.ecommProductId,
                }),
          },
        );
      }
      setProductEdited(false);
      setDisableRemoveButton(false);
      const { fulfillmentOptions } = skuDetailsData ?? {};
      const fullFillmaxQuantity =
        fulfillmentOptions?.find((option) => option.groupId == props.groupId)?.maxAvailableQty ?? 0;
      const allFulfillmentOptions =
        skuDetailsData?.skuPricingAndAvailability.skuAvailabilityInfo.fulfillmentOptions;
      const selectedFulfillmentOption = allFulfillmentOptions?.find((option) => option.selected);
      let startGroupId;

      const fulfillmentIdChange =
        fulfillmentId === FULFILLMENT_METHODS.SAMEDAY ? cartConstants.SDD : cartConstants.STH;
      const fulfillmentChange =
        props.groupId === FULFILLMENT_METHODS.STOREORDER
          ? cartConstants.BOPUS
          : fulfillmentIdChange;

      if ((skuDetailsData?.quantityInCart ?? 0) > 0) {
        startGroupId = selectedFulfillmentOption?.groupTypeId ?? -1;
      } else {
        const shipToHomeAvailable = checkAvailabilityByGroupTypeId(
          allFulfillmentOptions,
          FULFILLMENT_METHODS.ONLINEORDER,
        );
        const storePickupAvailable = checkAvailabilityByGroupTypeId(
          allFulfillmentOptions,
          FULFILLMENT_METHODS.STOREORDER,
        );
        const selectedShippingType = getFulfillmentOption(
          shipToHomeAvailable,
          storePickupAvailable,
          props.defaultFulfillmentOption,
          locale,
        );
        startGroupId =
          selectedShippingType === cartConstants.ONLINEORDER
            ? FULFILLMENT_METHODS.ONLINEORDER
            : FULFILLMENT_METHODS.STOREORDER;
      }

      if (
        ((prevGroupIdInCart == 0 && props.groupId === startGroupId) ||
          prevGroupIdInCart === props.groupId) &&
        selectedFulfillmentOption?.fulfillmentTypeId === fulfillmentId &&
        prevcartCount !== count &&
        fullFillmaxQuantity !== 1
      ) {
        if (!sessionStorage.getItem('sister_store_update_type')) {
          trackQuantityChange(
            'pdpPageQuantityChange',
            [String(fullFillmaxQuantity)],
            [String(productDetails?.itemId)],
            [String(count)],
          );
        }
      } else if (
        (prevGroupIdInCart !== props.groupId ||
          selectedFulfillmentOption?.fulfillmentTypeId !== fulfillmentId) &&
        prevcartCount !== count &&
        !(prevcartCount === 0 && count === 1) &&
        fullFillmaxQuantity !== 1
      ) {
        trackQuantityFulfillmentChange(
          fulfillmentChange,
          'pdpPageFulfillmentAndQuantityChange',
          [String(fullFillmaxQuantity)],
          [String(productDetails?.itemId)],
          [String(count)],
          'fulfillmentAndQuantityChange',
        );
        setPrevGroupIdInCart(props.groupId);
      } else if (
        prevGroupIdInCart !== props.groupId ||
        selectedFulfillmentOption?.fulfillmentTypeId !== fulfillmentId
      ) {
        trackFulfillmentChange(fulfillmentChange, 'pdpPageFulfillmentChange', [
          String(selectedFulfillmentTypeIdOriginal),
        ]);
        setPrevGroupIdInCart(props.groupId);
      }
    },
    [
      props,
      skuDetailsData,
      acesEnabled,
      productDetails?.partGroupId,
      productDetails?.ecommProductId,
      productDetails?.itemId,
      productDetails?.originalPartTypeId,
      productDetails?.subBrandName,
      productDetails?.partNumber,
      productDetails?.partTypeName,
      fulfillmentId,
      catalogVehicleId,
      storeNumber,
      xFusionQueryId,
      patchOrderItem,
      prevGroupIdInCart,
      isNewDealsApiEnabled,
      useOMSInventoryQohService,
      useGCPPriceService,
      queryClient,
      price,
      locale,
      selectedFulfillmentTypeIdOriginal,
    ],
  );

  const addToCart = useCallback(
    async (count: number = props.count) => {
      const productIdFromProductDetails =
        (acesEnabled ? productDetails?.partGroupId : productDetails?.ecommProductId) ?? '';
      const addToCartRequest = {
        sku_id: productDetails?.itemId ?? '',
        product_id: productIdFromProductDetails,
        quantity: String(count),
        category_id: productDetails?.categoryId ?? '',
        order_type: `${fulfillmentId}` as OrderType,
        catalog_vehicle_id: catalogVehicleId ?? '',
        store_number: String(storeNumber ?? ''),
        orderType: `${fulfillmentId}`,
        xFusionQueryId: xFusionQueryId ?? getXFusionQueryId() ?? '',
        positionId: productDetails?.positionId,
        brandId: productDetails?.brandId,
        uniqueId: productDetails?.uniqueId,
      };

      const fulfillmentIdChange =
        fulfillmentId === FULFILLMENT_METHODS.SAMEDAY ? cartConstants.SDD : cartConstants.STH;
      const fulfillmentChange =
        props.groupId === FULFILLMENT_METHODS.STOREORDER
          ? cartConstants.BOPUS
          : fulfillmentIdChange;

      const productType = root.sessionStorage?.getItem('productType');

      let analyticsData: AddToCartAnalyticsData | null = null;
      const discountedPrice = isNewDealsApiEnabled
        ? skuDealsData?.[0]?.skuDealsInfo?.discountedPrice
        : skuDetailsData?.skuPricingAndAvailability?.discountedPrice;
      const productStrikeThroughPricing = getProductStrikethroughAnalyticValue(
        skuDetailsData?.skuPricingAndAvailability,
        discountedPrice,
        strikeThroughPricingEnabled,
      );
      const { pageName = '', pageType = '' } = root.utag_data ?? {};
      const enhancedPDPContent = sessionStorage.getItem('enhancedPDPContent') === 'true';
      analyticsData = {
        fulfillmentChange: prevGroupId !== 0 ? fulfillmentChange : '',
        customLinkName:
          prevGroupId !== 0
            ? 'pdpPageFulfillmentChange'
            : productType === 'certona'
              ? 'PDP:Certona:Cart Addition'
              : productType === 'sponsoredProduct'
                ? 'PDP:SponsoredProduct:Cart Addition'
                : 'PDP:AZ Product:Cart Addition',
        productBrand: productDetails?.subBrandName ?? '',
        price: props.retailPrice,
        corePrice: (skuDetailsData?.skuPricingAndAvailability?.corePrice ?? 0) * count,
        originalPartTypeId: productDetails?.originalPartTypeId ?? '',
        productRepositoryId: productDetails?.ecommProductId ?? '',
        productPartNumber: productDetails?.partNumber ?? '',
        pageName: pageName,
        pageType: pageType,
        productRecommendationTypeList: getProductRecommendationType(productType),
        productStrikeThroughPricing: productStrikeThroughPricing,
        productFulfillmentOptionSeenByCustomer: selectedFulfillmentTypeIdOriginal,
        partGroupId: productDetails?.partGroupId ?? '',
        partTerminologyId: productDetails?.ecommProductId ?? '',
        enhancedPDPContent,
      };

      const isAffinityRecommendation = sessionStorage.getItem('pdpUtagViewAffinity') === 'true';

      if (isAffinityRecommendation && analyticsData) {
        analyticsData = {
          ...analyticsData,
          productRecommendationTypeList: 'Affinity',
        };
      }

      if (addToCartRequest?.product_id && !skuDetailsData?.itemPresentInCart) {
        const onSuccess = () => {
          props.setQuantityInCart(props.count);
          props.setTrackQOHError(true);
          setProductAddedToCart(true);
        };

        const onSettled = async () => {
          setDisableRemoveButton(false);
          cartButtonRef.current?.onSuccessfulAdd?.();
        };

        setDisableRemoveButton(true);
        onAddToCartCall(addToCartRequest, analyticsData, catalogVehicleId, onSuccess, onSettled);
      } else {
        router.push(routePaths.cart);
      }
    },
    [
      props,
      acesEnabled,
      productDetails?.partGroupId,
      productDetails?.ecommProductId,
      productDetails?.itemId,
      productDetails?.categoryId,
      productDetails?.subBrandName,
      productDetails?.originalPartTypeId,
      productDetails?.partNumber,
      productDetails?.positionId,
      productDetails?.brandId,
      productDetails?.uniqueId,
      fulfillmentId,
      catalogVehicleId,
      storeNumber,
      xFusionQueryId,
      isNewDealsApiEnabled,
      skuDealsData,
      skuDetailsData?.skuPricingAndAvailability,
      skuDetailsData?.itemPresentInCart,
      strikeThroughPricingEnabled,
      prevGroupId,
      selectedFulfillmentTypeIdOriginal,
      onAddToCartCall,
      router,
    ],
  );

  const addToCartDisabled = () => {
    if (isFetchingLocation) {
      return true;
    }

    const retailPrice = props.retailPrice?.toString().split('.');
    const dealPrice = [retailPrice[0], retailPrice[1] ? retailPrice[1].padEnd(2, '0') : '00'];
    const havePrice = !!dealPrice.reduce(
      (totalPrice, value) => totalPrice + Number(value || '0'),
      0,
    );
    const fulfillmentOptions = skuPricingAndAvailability?.skuAvailabilityInfo?.fulfillmentOptions;
    const shipToHomeAvailable = checkAvailabilityByGroupTypeId(
      fulfillmentOptions,
      fulfillmentGroupTypeIds.shipToHome,
    );
    const storePickupAvailable = checkAvailabilityByGroupTypeId(
      fulfillmentOptions,
      fulfillmentGroupTypeIds.storePickup,
    );
    const productAvailableQuantity =
      (fulfillmentOptions && getMaxAvailQtyFromOptions(fulfillmentOptions)) ?? 0;
    const isNotAvailable = fulfillmentOptions?.every((item) => {
      return item.available === false;
    });
    if (
      isNotAvailable &&
      sisterStoreAvailable &&
      isBopusNewMsgApplicable &&
      storeNumber &&
      storeNumber !== defaultStoreUS
    ) {
      return false;
    }
    return (
      (isMxWeb && storeSelected && (!storePickupAvailable || productAvailableQuantity <= 0)) ||
      (!isMxWeb && customizeSKUs && !shipToHomeAvailable) ||
      (!isMxWeb && !havePrice && !customizeSKUs) ||
      (!isMxWeb && !shipToHomeAvailable && !storePickupAvailable)
    );
  };

  const analyticsBVRating = () => {
    try {
      const ratingElement = document.getElementsByClassName('bv_avgRating')[0] as
        | HTMLElement
        | undefined;
      const reviewCountElement = document.getElementsByClassName('bv_numReviews_text')[0] as
        | HTMLElement
        | undefined;

      if (ratingElement && reviewCountElement) {
        const rating = ratingElement.innerText;
        const reviewCount = reviewCountElement.innerText;

        if (rating && reviewCount) {
          analyticsPDPReviewRating(rating, reviewCount);
        } else {
          // eslint-disable-next-line no-console
          console.warn('BV rating analytics: Rating or review count is empty');
        }
      }
    } catch (error) {
      logger.error({
        message: 'Error processing BV rating analytics',
        meta: error,
      });
    }
  };

  const showWarrantyModal = () => {
    props.showWarrantyModal(productInfoConstants.ProductSpec);
  };

  const goToCart = () => {
    router.push(routePaths.cart);
  };

  const toggleStoreModal = () => {
    setStoreModalOpen(!storeModalOpen);
  };

  const showBundleDealsDrawer = () => {
    props.setShowBundleDeals(true);
    if (!props.preferredVehicle && vehicleSpecific) {
      props.setShowYmmeDealsDrawer(true);
    }
  };

  const handleShowYmmeFitment = (
    hasVehicle: boolean,
    vehicleFits: boolean,
    vehicleFit?: string | undefined,
  ) => {
    if (vehicleFit === 'NEEDS_MORE_INFO') {
      setInteractionLocation('needsMoreInfo');
      pdpCheckFits = 'Need more info';
    } else if (hasVehicle && vehicleFits) {
      setInteractionLocation('fitmentThisFits');
      pdpCheckFits = 'Fits your vehicle';
    } else if (hasVehicle && !vehicleFits) {
      setInteractionLocation('fitmentThisDoesNotFit');
      pdpCheckFits = 'Does not Fit your X';
    } else {
      setInteractionLocation('fitmentCheckFits');
      pdpCheckFits = 'Check if it fits';
    }
    setShowYmme(true);
  };

  /* EFFECTS */
  useEffect(() => {
    setShouldShowFulfillmentOptions(true);
  }, []);
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          document.getElementById('paypal-message');
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const setStoreAndAddToCart = async () => {
      const { setGroupId, groupId, count } = props;
      const { fulfillmentOptions } = skuDetailsData ?? {};
      const fullFillmaxQuantity =
        fulfillmentOptions?.find((option) => option.groupId == FULFILLMENT_METHODS.STOREORDER)
          ?.maxAvailableQty ?? 0;
      props.setShippingType(cartConstants.STOREORDER);
      changeShippingTypeV2(
        cartConstants.STOREORDER,
        FULFILLMENT_METHODS.STOREORDER,
        fullFillmaxQuantity,
      );
      setGroupId(FULFILLMENT_METHODS.STOREORDER);
      if (groupId === FULFILLMENT_METHODS.STOREORDER) {
        const quantity = Number(sessionStorage.getItem('quantity'));
        const sanitizedQuantity = isNaN(quantity) ? count : quantity;
        skuDetailsData?.itemPresentInCart
          ? updateCart(sanitizedQuantity)
          : addToCart(sanitizedQuantity);
        sessionStorage.removeItem('addOrUpdateCart');
        sessionStorage.removeItem('skuId');
        sessionStorage.removeItem('quantity');
        sessionStorage.removeItem('productId');
        sessionStorage.removeItem('storeNumber');
      }
    };

    if (
      sessionStorage.getItem('addOrUpdateCart') === 'true' &&
      sessionStorage.getItem('skuId') === productDetails?.itemId &&
      !sisterStoreContext.isSettingNewStore
    ) {
      setStoreAndAddToCart();
    }
  }, [
    productDetails?.itemId,
    changeShippingTypeV2,
    props,
    skuDetailsData,
    updateCart,
    addToCart,
    sisterStoreContext.isSettingNewStore,
  ]);

  useEffect(() => {
    setTimeout(() => {
      if (!isCustomReviewsEnabled) {
        analyticsBVRating();
      }

      const pageType = legacyRouter.query.pageType ?? '';
      if (pageType === pageTypes.ProductDetail) {
        ratingView();
      }
    }, 1000);
  }, [legacyRouter.query, isCustomReviewsEnabled]);

  useEffect(() => {
    const allFulfillmentOptions =
      skuDetailsData?.skuPricingAndAvailability.skuAvailabilityInfo.fulfillmentOptions;
    const storePickupAvailable = checkAvailabilityByGroupTypeId(
      allFulfillmentOptions,
      FULFILLMENT_METHODS.STOREORDER,
    );
    const { storePickupStockLabel = '' } = skuDetailsData?.skuPricingAndAvailability ?? {};
    if (
      storePickupAvailable &&
      (storePickupStockLabel === productInfoConstants.outOfStock ||
        storePickupStockLabel === productInfoConstants.notAvailable)
    ) {
      props.changeFindProduct();
    }
  }, [props, skuDetailsData?.skuPricingAndAvailability]);

  useEffect(() => {
    skuAvailableQuantities.current = getAllAvailableQuantitiesForSku(
      skuDetailsData?.fulfillmentOptions,
    );
  }, [skuDetailsData?.fulfillmentOptions]);

  useEffect(() => {
    if (props.pdpAddToCartSuccess) {
      if (!isDesktop) {
        callToCertona({
          certonaType: certonaPageType.cartAdd,
          vehicleID: catalogVehicleId || '',
          skuIds: productDetails?.itemId,
        });
      }
    }
  }, [catalogVehicleId, productDetails?.itemId, props.pdpAddToCartSuccess, isDesktop]);

  useEffect(
    () => {
      if (
        !!skuDetailsData &&
        (productDetails?.itemId !== prevSkuId ||
          (props.shippingType !== props.prevShippingType && !productEdited))
      ) {
        const { quantityInCart = 0, skuPricingAndAvailability } = skuDetailsData ?? {};
        const startCount = quantityInCart > 0 ? quantityInCart : 1;
        const currentPrice = getStaticFormattedPrice(
          skuPricingAndAvailability?.configurableSku,
          skuPricingAndAvailability,
        );
        props.setCount(startCount);
        props.setQuantityInCart(quantityInCart);
        props.setRetailPrice(getFormattedPriceMultiplied(currentPrice, startCount));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      productDetails?.itemId,
      productEdited,
      props.shippingType,
      props.prevShippingType,
      skuDetailsData,
      prevSkuId,
    ],
  );

  useEffect(
    () => {
      if (
        !!skuDetailsData &&
        (!props.shippingType ||
          props.productDetailsUpdatedAt !== prevProductDetailsUpdate ||
          props.skuDetailsDataLastUpdatedAt !== prevSkuDetailsUpdate)
      ) {
        const { quantityInCart = 0, skuPricingAndAvailability } = skuDetailsData ?? {};
        const startCount = quantityInCart > 0 ? quantityInCart : 1;
        const currentPrice = getStaticFormattedPrice(
          skuPricingAndAvailability?.configurableSku,
          skuPricingAndAvailability,
        );
        props.setCount(startCount);
        props.setQuantityInCart(quantityInCart);
        props.setRetailPrice(getFormattedPriceMultiplied(currentPrice, startCount));

        if (!props.shippingTypeChanged) {
          setProductEdited(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.shippingType,
      props.productDetailsUpdatedAt,
      props.skuDetailsDataLastUpdatedAt,
      skuDetailsData,
      prevProductDetailsUpdate,
      prevSkuDetailsUpdate,
    ],
  );

  const handleFulfillmentIdUpdate = useCallback(
    (newFulfillmentId: number) => {
      props.setFulfillmentId(newFulfillmentId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (!props.shippingTypeChanged && !productEdited) {
      const quantityInCart = skuDetailsData?.quantityInCart ?? 0;
      const allFulfillmentOptions =
        skuDetailsData?.skuPricingAndAvailability.skuAvailabilityInfo.fulfillmentOptions;
      const selectedFulfillmentId = getSelectedFulfilmentForSelectedQty(
        props.count,
        allFulfillmentOptions,
        props.shippingType,
      );
      if (quantityInCart > 0 && selectedFulfillmentId) {
        handleFulfillmentIdUpdate(selectedFulfillmentId);
      } else if (fulfillmentId === FULFILLMENT_METHODS.NEXTDAY && props.count > nddQuantity) {
        handleFulfillmentIdUpdate(FULFILLMENT_METHODS.ONLINEORDER);
      }
    }
  }, [
    handleFulfillmentIdUpdate,
    props.shippingTypeChanged,
    props.count,
    props.shippingType,
    shouldShowSkeleton,
    fulfillmentId,
    skuDetailsData,
    productEdited,
    nddQuantity,
  ]);

  useEffect(
    () => {
      if (
        !shouldShowSkeleton &&
        (!props.shippingType ||
          props.productDetailsUpdatedAt !== prevProductDetailsUpdate ||
          props.skuDetailsDataLastUpdatedAt !== prevSkuDetailsUpdate)
      ) {
        const quantityInCart = skuDetailsData?.quantityInCart ?? 0;
        const allFulfillmentOptions =
          skuDetailsData?.skuPricingAndAvailability.skuAvailabilityInfo.fulfillmentOptions;
        const shipToHomeAvailable = checkAvailabilityByGroupTypeId(
          allFulfillmentOptions,
          FULFILLMENT_METHODS.ONLINEORDER,
        );
        const storePickupAvailable = checkAvailabilityByGroupTypeId(
          allFulfillmentOptions,
          FULFILLMENT_METHODS.STOREORDER,
        );
        if (quantityInCart > 0) {
          const selectedFulfillmentOption = allFulfillmentOptions?.find(
            (option) => option.selected,
          );
          const startGroupId = selectedFulfillmentOption?.groupTypeId ?? -1;
          const startShippingType = convertFulfillmentTypeToOrderType(startGroupId);
          props.setShippingType(startShippingType);
          props.setGroupId(startGroupId);

          const selectedFulfillmentId = getSelectedFulfilmentForSelectedQty(
            props.count,
            allFulfillmentOptions,
            props.shippingType,
          );
          const fulfillmentIdInCart = getSelectedFulfillmentInCart(
            props.count,
            allFulfillmentOptions,
          );
          const addedToCart = quantityInCart > 0;
          if (
            (fulfillmentIdInCart !== selectedFulfillmentId ||
              (fulfillmentIdInCart === undefined && selectedFulfillmentId === undefined)) &&
            !props.shippingTypeChanged &&
            !(productAddedToCart || addedToCart)
          ) {
            const storePickupFulfillmentId = storePickupAvailable
              ? FULFILLMENT_METHODS.STOREORDER
              : '';
            const availableFulfillmentId =
              startGroupId === FULFILLMENT_METHODS.ONLINEORDER && shipToHomeAvailable
                ? (selectedFulfillmentId ?? FULFILLMENT_METHODS.ONLINEORDER)
                : storePickupFulfillmentId;
            if (availableFulfillmentId) {
              handleFulfillmentIdUpdate(availableFulfillmentId);
              setProductEdited(true);
            }
          } else if (!sisterStoreContext.isSisterStoreFlyoutOpen) {
            handleFulfillmentIdUpdate(selectedFulfillmentId!);
            setProductEdited(false);
          }
        } else if (!props.shippingTypeChanged || props.storeChanged) {
          if (props.storeChanged) {
            props.setStoreChanged(false);
          }
          let storeData = allFulfillmentOptions?.find(
            (option) => option.groupTypeId === FULFILLMENT_METHODS.STOREORDER,
          );
          const defaultShippingId = getFulfillmentId(
            shipToHomeAvailable,
            storePickupAvailable,
            props.defaultFulfillmentOption,
            nddQuantity > 0,
            sddQuantity > 0,
            storeData?.sisterStoreAvailable,
          );
          const selectedShippingType = getFulfillmentOption(
            shipToHomeAvailable,
            storePickupAvailable,
            props.defaultFulfillmentOption,
            locale,
          );
          handleFulfillmentIdUpdate(defaultShippingId);

          const isNotAvailable = allFulfillmentOptions?.every(
            (option) => option.available === false,
          );
          const sisterStoreAvailable = allFulfillmentOptions?.[0]?.sisterStoreAvailable ?? false;
          if (isNotAvailable && sisterStoreAvailable) {
            props.setShippingType(cartConstants.STOREORDER);
          } else {
            props.setShippingType(selectedShippingType);
          }
          props.setGroupId(
            selectedShippingType === cartConstants.ONLINEORDER
              ? FULFILLMENT_METHODS.ONLINEORDER
              : FULFILLMENT_METHODS.STOREORDER,
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleFulfillmentIdUpdate,
      prevProductDetailsUpdate,
      prevSkuDetailsUpdate,
      skuDetailsData,
      productAddedToCart,
      props.shippingType,
      props.productDetailsUpdatedAt,
      props.shippingTypeChanged,
      sisterStoreContext.isSisterStoreFlyoutOpen,
      shouldShowSkeleton,
    ],
  );

  useEffect(() => {
    setSelectedFulfillmentTypeIdOriginal(selectedItemId ?? 0);
  }, [selectedItemId]);

  useEffect(() => {
    const showReview = document.getElementById('ratingsTop');
    //listen for onclick events for showReview
    if (showReview) {
      showReview.addEventListener(
        'click',
        () => {
          handleShowReviewAnalytics();
        },
        false,
      );
    }
    return () => {
      if (showReview) {
        showReview.removeEventListener('click', () => {}, false);
      }
    };
  }, [handleShowReviewAnalytics]);

  /* UI */
  const displayDeals = () => {
    if (!pdpDealsModelOpen) {
      return null;
    }
    const mainImage = productDetails?.itemImageUrl ?? '';
    const productId = productDetails?.ecommProductId ?? '';
    const catalogRefIdNew = productDetails?.itemId ?? '';
    const skuDescription = productDetails?.itemDescription ?? '';
    return (
      <DealsModal
        openDialog={pdpDealsModelOpen}
        handleCloseModal={handleCloseModal}
        mainImage={mainImage}
        catalogRefId={catalogRefIdNew}
        productId={productId}
        productDetails={{
          productId,
          skuId: catalogRefIdNew,
          skuDescription,
        }}
      />
    );
  };

  const pdpDealsButton = () => {
    return (
      dealAvailable &&
      !isDealBadgeEnabled && (
        <Grid item xs={12} lg={12} className={styles.dealsButtonStyle}>
          <Button variant="ghost" rightArrow onClick={handleDealsModal} tabIndex={0}>
            <span className={styles.imgDealsLink}>
              <NextImage
                src={itemDetail.dealIcon.src}
                alt={itemDetail.dealIcon.alt}
                role="presentation"
                width={16}
                height={16}
              />
            </span>
            <span className={cx(azCommonStyles['az-button-text'], styles.specialDealsLink)}>
              {labels.viewDealsItem}
            </span>
          </Button>
        </Grid>
      )
    );
  };

  const pdpPartNumber = () => {
    return (
      <View
        direction="row"
        attributes={{
          id: 'pdp-part-number',
        }}
      >
        <View.Item>
          <Text variant="small-body" weight="bold">
            {`${labels.lblProductPartNumber} #`}
          </Text>
        </View.Item>
        <View.Item>
          <Text variant="small-body">{partNumberDisplay}</Text>
        </View.Item>
      </View>
    );
  };

  const pdpRatings = () => {
    return isCustomReviewsEnabled ? (
      <ProductStatistics
        productDetails={productDetails}
        reviewStatistic={reviewStatistics}
        reviewsPending={reviewsPending}
        unsetStyles
      />
    ) : catalogRefIdNew && isUS ? (
      <div>
        <div
          id="ratingsTop"
          data-bv-show="rating_summary"
          data-bv-product-id={catalogRefIdNew}
          aria-label="ratings and reviews"
          onClick={ratingView}
          data-testid="ratingsTop"
        />
      </div>
    ) : null;
  };

  const pdpWarranty = () => {
    return (
      <WarrantyBadge
        warranty={skuDetailsData?.warranty || productDetails?.warrantyMonths || ''}
        onClick={showWarrantyModal}
        classes={{
          button: styles.warrantyButton,
          label: styles.warrantyText,
        }}
      />
    );
  };

  const pdpTechNotes = useMemo(() => {
    const notes = getFormattedDisplayNotes({
      ...(productDetails || {}),
      showFitmentNotesOnly: false,
    });

    return !notes ? null : <PdpTechNotesV2 techNotes={notes!} />;
  }, [productDetails]);

  const renderFitmentBadge = () => {
    if (shouldShowSkeleton && !acesEnabled) {
      return <Skeleton height="40px" width="100%" borderRadius="small" />;
    }

    const { hasVehicle } = props;
    const fittingPartsURL =
      breadCrumbs?.find(
        (item) =>
          item.pageType === 'ProductShelf' || (acesEnabled && item.pageType === 'PartGroup'),
      )?.seoUrl ?? routePaths.blankURL;

    return acesEnabled ? (
      <ACESFitmentButton
        customClass={{ container: styles.fitmentButton }}
        vehicleFitment={productDetails?.vehicleFit}
        toggleShowYmme={() =>
          handleShowYmmeFitment(hasVehicle, vehicleFits, productDetails?.vehicleFit)
        }
        onClickSeeFittingParts={async () => {
          await setPageSpecificDataAsync({
            productFindingMethod: 'Finder',
            pfmLocation: 'Product Fitment Banner',
            eventType: 'productFitmentBannerClick',
            finderFeature: 'Parts that Fit Product Card',
          });
          router.push(fittingPartsURL);
        }}
        productName={itemDescription}
      />
    ) : (
      <FitmentButton
        customClass={styles.fitmentButton}
        hasVehicle={hasVehicle}
        fitsVehicle={vehicleFits}
        toggleShowYmme={() => handleShowYmmeFitment(hasVehicle, vehicleFits)}
        onClickSeeFittingParts={async () => {
          await setPageSpecificDataAsync({
            productFindingMethod: 'Product Fitment Banner',
            pfmLocation: 'Product Fitment Banner',
            eventType: 'productFitmentBannerClick',
          });
          router.push(fittingPartsURL);
        }}
      />
    );
  };

  /**
   * Fulfillment options block - shared between desktop and mobile/tablet layouts
   * Contains: BOPUS, Home Delivery, Same Day Delivery option buttons
   */
  const pdpFulfillmentOptionsBlock = () => {
    const fulFillmentOptionsV2 = skuDetailsData?.fulfillmentOptions;
    const configurableSku = skuDetailsData?.skuPricingAndAvailability?.configurableSku ?? false;
    const lowestPrice = skuPricingAndAvailability?.lowestPrice ?? '';
    const [dollars, cents = '00'] = configurableSku
      ? lowestPrice.toString().split('.')
      : staticPrice.toString().split('.');
    const price = parseFloat(`${dollars}.${cents}`);
    const availabilityInfoVO = skuDetailsData?.availabilityInfoVO;

    // Skeleton state
    if (shouldShowSkeleton || !shouldShowFulfillmentOptions) {
      return (
        <>
          <View gap={2} direction="row" padding={[0, 0, 4, 0]}>
            <View.Item grow>
              <Skeleton height="94px" width="100%" />
            </View.Item>
            <View.Item grow>
              <Skeleton height="94px" width="100%" />
            </View.Item>
            <View.Item grow>
              <Skeleton height="94px" width="100%" />
            </View.Item>
          </View>
          <Skeleton width="45%" height="19px" />
        </>
      );
    }

    // US locale
    if (locale !== countryCodes.mx && locale !== countryCodes.ptBr) {
      return (
        <FulfillmentOptionButtonsV3
          fulfillmentOptionsGroupList={fulFillmentOptionsV2 ?? []}
          itemPresentInCart={skuDetailsData?.itemPresentInCart}
          changeShippingType={changeShippingTypeV3}
          shippingType={props.shippingType}
          productName={productDetails?.itemDescription ?? ''}
          skuId={skuPricingAndAvailability?.skuId}
          isDealDrawer={false}
          quantityInCart={skuDetailsData?.quantityInCart ?? 0}
          price={price}
          count={props.count}
          sddQuantity={sddQuantity}
          nddQuantity={nddQuantity}
          fulfillmentId={fulfillmentId}
          disableRemoveButton={disableRemoveButton}
        />
      );
    }

    // MX/BR locale
    const saveNowOrRebateBadges = (
      <ProductAvailabilityInfoBadge
        dealAvailable={dealAvailable ?? false}
        rebatesAvailable={rebatesAvailable}
        rebateUrl={rebateUrl ?? undefined}
        rebateShortDesc={rebateShortDesc ?? undefined}
        dealDrawerPDPEnabled={isDealBadgeEnabled}
        onDealsClick={handleDealsModal}
      />
    );

    return (
      <View>
        <FulfillmentOptionsMX
          hasDealBadge={!!saveNowOrRebateBadges}
          changeShippingType={(val: string) => props.setShippingType(val)}
          shippingType={props.shippingType}
          availabilityInfoVO={availabilityInfoVO}
          dollars={dollars}
          cents={cents}
          skuPricingAndAvailability={skuPricingAndAvailability}
          quantityInCart={skuDetailsData?.quantityInCart ?? 0}
          skuId={skuPricingAndAvailability?.skuId}
          productName={productDetails?.itemDescription ?? ''}
          storeSelected={!!storeSelected}
          itemData={productDetails}
          inline
        />
      </View>
    );
  };

  /**
   * Price display block for modular layout
   * Renders: Price, core charge, deal badges, PayPal messaging, rewards badge, deal drawer button
   * NOTE: Does NOT include quantity stepper (use pdpQuantityStepper() for that)
   */
  const pdpPriceDisplay = () => {
    // Brazil early return
    if (locale === countryCodes.ptBr) {
      return null;
    }

    // Loading state return skeleton
    if (shouldShowSkeleton) {
      return (
        <>
          <View
            gap={6}
            direction="row"
            padding={[0, 0, locale == countryCodes.us ? 9 : 0, 0]}
            justify="space-between"
            attributes={{ style: { paddingBottom: '0px' } }}
          >
            <Skeleton height="31px" width="180px" />
            <Skeleton height="31px" width="80px" />
          </View>
          <View direction="column" gap={4} attributes={{ style: { paddingBottom: '24px' } }}>
            <View
              gap={2}
              direction="row"
              attributes={{
                style: {
                  paddingLeft: '0px',
                  paddingRight: '0px',
                  paddingBottom: '20px',
                  paddingTop: '12px',
                  borderBottom: '1px solid #e5e5e5',
                },
              }}
            >
              <Skeleton
                height="16px"
                width="16px"
                attributes={{ style: { borderRadius: '50%', minHeight: '16px', marginTop: '6px' } }}
              />
              <Skeleton height="19px" width="308px" />
            </View>
          </View>
        </>
      );
    }
    // Local calculations (price formatting)
    const configurableSku = skuDetailsData?.skuPricingAndAvailability?.configurableSku ?? false;
    const lowestPrice = skuPricingAndAvailability?.lowestPrice ?? '';
    const [dollars, cents = '00'] = configurableSku
      ? lowestPrice.toString().split('.')
      : staticPrice.toString().split('.');
    const price = parseFloat(`${dollars}.${cents}`);

    // Deal drawer button logic
    const selectedGroupFulfillmentHasDealsAvailableQty =
      skuPricingAndAvailability?.skuAvailabilityInfo?.fulfillmentOptions?.find(
        (o) => o.available && o.groupTypeId === props.groupId && o.dealsAvailableQty > 0,
      );
    const showStrikeThroughPricing = strikeThroughPricingEnabled && !!discountedPrice;
    const showDealDrawerButton =
      isDealBadgeEnabled &&
      storeSelected &&
      !!props.groupId &&
      !!selectedGroupFulfillmentHasDealsAvailableQty &&
      !showStrikeThroughPricing;
    const showDealDrawerButtonV2 =
      isDealBadgeEnabled &&
      storeSelected &&
      !showStrikeThroughPricing &&
      !!props.groupId &&
      skuDealsData?.[0]?.skuDealsInfo?.dealAvailable;

    return (
      <View direction="column">
        <View.Item>
          {!hidePricing && !shouldShowSkeleton && (
            <PdpPriceQuantity
              addedToCart={addedToCart}
              dealAvailable={dealAvailable ?? false}
              configurableSku={configurableSku ?? false}
              discountedPrice={discountedPrice ?? null}
              retailPrice={props.retailPrice?.toString().split('.')}
              availProduct={skuPricingAndAvailability}
              count={props.count}
              staticPrice={staticPrice}
              saveNowOrRebateBadges={
                <ProductAvailabilityInfoBadge
                  dealAvailable={dealAvailable ?? false}
                  rebatesAvailable={rebatesAvailable}
                  rebateUrl={rebateUrl ?? undefined}
                  rebateShortDesc={rebateShortDesc ?? undefined}
                  dealDrawerPDPEnabled={isDealBadgeEnabled}
                  onDealsClick={handleDealsModal}
                />
              }
              noGridSpacing={true}
              unsetPriceMargin={true}
            />
          )}
        </View.Item>
        {!!props.shippingType &&
          (isNewDealsApiEnabled ? showDealDrawerButtonV2 : showDealDrawerButton) && (
            <View.Item gapBefore={{ s: 3, m: 3 }}>
              <DealsDrawerButton
                seeDealsButtonRef={seeDealsButtonRef}
                handleClick={showBundleDealsDrawer}
                autoFocus={autoFocus}
                dealConfigurableLabel={
                  isNewDealsApiEnabled
                    ? skuDealsData?.[0]?.skuDealsInfo?.dealConfigurableLabel
                    : skuDetailsData?.skuPricingAndAvailability?.dealConfigurableLabel
                }
                customClass={styles.dealsDrawerButtonV2}
                variant="calm"
              />
            </View.Item>
          )}
        {locale === countryCodes.enUS && (
          <View.Item gapBefore={3}>
            <PaypalPayLaterMessaging pageType="catalog_product-detail" price={price} />
          </View.Item>
        )}
        <View.Item>
          {horizontalRule(6)}
          {/* Quantity warning notification */}
          {props.count > 0 && displayQuantityWarningMessage && (
            <MaxQuantityNotificationBlockPdpV2
              selectedFulfillmentID={fulfillmentId}
              availableQuantity={
                availableQuantity && availableQuantity > 99 ? 99 : availableQuantity
              }
              selectedQuantity={props.count}
              nddQuantity={nddQuantity}
              sddQuantity={sddQuantity}
              skuAvailabilityInfo={skuPricingAndAvailability?.skuAvailabilityInfo}
            />
          )}
        </View.Item>
      </View>
    );
  };

  /**
   * Super SKU selector block for modular layout
   * Renders: SuperSkuOptionSelector components for each available option
   * Returns null if super SKUs are not enabled or no data available
   */
  const pdpSuperSkuSelector = () => {
    if (!isSuperSkusEnabled || !productDetails?.availableOptions || !productDetails?.choices) {
      return null;
    }
    const optionKeys = Object.keys(productDetails?.availableOptions ?? {});

    const optionsWithoutImages = optionKeys.filter((option) => {
      const values = productDetails?.availableOptions?.[option] ?? [];
      return values.every((v) => !v?.imageUrl);
    });

    const optionsWithImages = optionKeys.filter((option) => {
      const values = productDetails?.availableOptions?.[option] ?? [];
      return values.some((v) => v?.imageUrl);
    });

    return (
      <View direction="column">
        {isSuperSkusEnabled && !!productDetails?.availableOptions && !!productDetails?.choices && (
          <View.Item>
            <View direction="column" className={styles.superSkuOptionsImagesContainer}>
              {[...optionsWithoutImages, ...optionsWithImages].map((option, index) => (
                <View.Item key={`${option}-${index}`}>
                  <SuperSkuOptionSelector
                    optionName={option}
                    availableOptions={productDetails.availableOptions}
                    choices={productDetails.choices}
                    currentSelection={currentSuperSkuSelection}
                    shouldShowSkeleton={shouldShowSkeleton}
                  />
                </View.Item>
              ))}
            </View>
          </View.Item>
        )}
      </View>
    );
  };

  /**
   * Quantity stepper block for modular layout
   * Renders: Quantity input with increment/decrement buttons
   * Used with pdpAddToCartCTA() in row layout
   */
  const pdpQuantityStepper = () => {
    if (locale === countryCodes.ptBr) {
      return null;
    }

    if (shouldShowSkeleton) {
      return <Skeleton width="120px" height="54px" borderRadius="small"></Skeleton>;
    }

    const configurableSku = skuDetailsData?.skuPricingAndAvailability?.configurableSku ?? false;

    return (
      <CalmWrapper
        configurableSku={configurableSku}
        discountedPrice={discountedPrice ?? null}
        retailPrice={props.retailPrice?.toString().split('.')}
        availProduct={skuPricingAndAvailability}
        customizeSKUs={customizeSKUs}
        count={props.count}
        staticPrice={staticPrice}
        handleQuantityChange={handleQuantityChange}
        availabilityCheck={skuAvailableQuantities.current}
        sisterStoreAvailable={sisterStoreAvailable}
        skuId={skuPricingAndAvailability?.skuId ?? ''}
        eligibleForNextDay={skuDetailsData?.aZBadgesFlagVO?.dealAvailable}
        shippingType={props.shippingType}
        setFulfillmentId={props.setFulfillmentId}
        pageType="productDetails"
        fulfillmentId={fulfillmentId}
        quantityInputDisabled={!isAvailable || isFetchingLocation}
        productTitle={productDetails?.itemDescription ?? ''}
        stepperButtonClassName={styles.stepperButtons}
      />
    );
  };

  /**
   * Add to Cart CTA block for modular layout
   * Renders: Add to Cart button with all logic from buttonAddToCart()
   * Handles all button states and click actions (7 different behaviors)
   * Used with pdpQuantityStepper() in row layout
   */
  const pdpAddToCartCTA = () => {
    if (locale === countryCodes.ptBr) {
      return null;
    }

    const preferredVehicle = props.preferredVehicle;

    const ariaLabelAddProductToCart = `${labels.lblAdd} ${productDetails?.itemDescription} ${labels.lblToCart}`;
    const ariaLabelCheckNearbyStores = `${labels.label_check_nearby_stores} ${labels.label_for} ${productDetails?.itemDescription} `;
    const ariaLabelUpdateCartWithQuantityProduct = interpolateLabel(
      labels.labelUpdateCartToProductItems,
      {
        quantity: props.count,
        product: productDetails?.itemDescription,
      },
    );

    const calcButtonLabel = () => {
      if (customizeSKUs && preferredVehicle) {
        return labels.customizeYourFloorMats;
      }
      if (customizeSKUs && !preferredVehicle) {
        return labels.lblSetVehicleToSeePricing;
      }

      const isStoreOrder = fulfillmentId === FULFILLMENT_METHODS.STOREORDER;

      if (
        storeData?.sisterStoreAvailable &&
        !storeData?.available &&
        isStoreOrder &&
        checkoutNearbyStoresEnabled &&
        storeNumber &&
        storeNumber !== defaultStoreUS
      ) {
        return labels.label_check_nearby_stores;
      }
      if (addedToCart || productAddedToCart) {
        if (productEdited && (updateCartEnabled || isBopusMexicoStoreEnabled)) {
          return labels.label_update_cart;
        } else {
          return labels.lblGoToCart;
        }
      }

      if (isMxWeb && !storeSelected) {
        return labels.labelSeeProductPrice;
      }
      return labels.lblAddToCart;
    };

    const calcAriaLabel = () => {
      if (customizeSKUs && preferredVehicle) {
        return labels.customizeYourFloorMats;
      }
      if (customizeSKUs && !preferredVehicle) {
        return labels.lblSetVehicleToSeePricing;
      }
      const isStoreOrder = fulfillmentId === FULFILLMENT_METHODS.STOREORDER;
      if (
        storeData?.sisterStoreAvailable &&
        !storeData?.available &&
        isStoreOrder &&
        checkoutNearbyStoresEnabled &&
        storeNumber &&
        storeNumber !== defaultStoreUS
      ) {
        return ariaLabelCheckNearbyStores;
      }
      if (addedToCart || productAddedToCart) {
        if (productEdited && (updateCartEnabled || isBopusMexicoStoreEnabled)) {
          return ariaLabelUpdateCartWithQuantityProduct;
        } else {
          return labels.lblGoToCart;
        }
      }

      if (isMxWeb && !storeSelected) {
        return labels.labelSeeProductPrice;
      }
      return ariaLabelAddProductToCart;
    };

    const handleOnClick = () => {
      const isStoreOrder = fulfillmentId === FULFILLMENT_METHODS.STOREORDER;
      if (
        storeData?.sisterStoreAvailable &&
        !storeData?.available &&
        isStoreOrder &&
        storeNumber &&
        storeNumber !== defaultStoreUS
      ) {
        sisterStoreContext.toggleSisterStoreFlyout({
          skuId: skuPricingAndAvailability?.skuId ?? '',
          shippingType: props.shippingType,
          productDetails: skuDetailsData ?? undefined,
          quantityInCart: quantityInCart,
        });
      } else if (isMxWeb && !storeSelected) {
        return toggleStoreModal();
      } else if (customizeSKUs && preferredVehicle) {
        const availability = storeData?.availableQty.toString();
        const fulfillmentIdStr = storeData?.fulfillmentTypeId.toString();
        trackCustomizeSkuStart(productDetails!, skuDetailsData!, fulfillmentIdStr!, availability!);
        return props.openConfigurableSku();
      } else if (customizeSKUs && !preferredVehicle) {
        setShowYmme(true);
        return;
      } else if (productEdited && (updateCartEnabled || isBopusMexicoStoreEnabled)) {
        return updateCart();
      } else if (productAddedToCart) {
        return goToCart();
      } else {
        return addToCart();
      }
    };

    const disabled = addToCartDisabled();

    return (
      <>
        <CartButton
          ref={cartButtonRef}
          variant="contained"
          disabled={disabled}
          onClick={handleOnClick}
          fullWidth={true}
          loading={props.addToCartButtonLoading || shouldShowSkeleton}
          data-testid="cart-action-pdp"
          ariaLabel={calcAriaLabel()}
          className={cx(styles.addToCartButton, { [styles.addToCartButtonDisabled]: disabled })}
        >
          {calcButtonLabel()}
        </CartButton>
        {storeModalOpen && <LazySearchStoreModal closeStoreModal={toggleStoreModal} />}
      </>
    );
  };

  /**
   * Left column content for desktop 3-column layout
   * Contains: ProductCarousel, vehicle banners
   */
  const renderLeftColumn = () => {
    return <ProductCarouselV2 data={productDetails!} labels={labels} />;
  };

  /**
   * Middle column content for desktop 3-column layout
   * Contains: Title, ratings, fitment, badges, super SKUs, specs, features, notes
   */
  const renderMiddleColumn = () => {
    const productTitle = productDetails?.itemDescription ?? '';
    const productBrand = productDetails?.brandName ?? '';
    const brandURL = productDetails?.brandUrl ?? '';

    const optionKeys = Object.keys(productDetails?.availableOptions ?? {});

    const optionsWithoutImages = optionKeys.filter((option) => {
      const values = productDetails?.availableOptions?.[option] ?? [];
      return values.every((v) => !v?.imageUrl);
    });

    const optionsWithImages = optionKeys.filter((option) => {
      const values = productDetails?.availableOptions?.[option] ?? [];
      return values.some((v) => v?.imageUrl);
    });

    return (
      <View direction="column">
        {previouslyBoughtItem?.transactionDate ? (
          <View.Item>
            <PurchasedDate purchasedDate={previouslyBoughtItem.transactionDate} />
          </View.Item>
        ) : null}
        <View.Item>
          <PdpTitleNotesV2
            productTitle={productTitle}
            productBrand={productBrand}
            brandURL={brandURL}
            shouldShowSkeleton={shouldShowSkeleton}
            productDetails={productDetails}
            purchaseDatePaddingEnabled={!!previouslyBoughtItem?.transactionDate}
          />
        </View.Item>
        <SeoContent productBrand={productBrand} catalogRefIdNew={catalogRefIdNew} />
        {!!oemPartNumber && (
          <View.Item gapBefore={{ s: 2, m: 3, l: 2, xl: 2 }}>
            <ProductCrossReference
              oemBrandName={oemBrandName || ''}
              oemPartNumber={oemPartNumber || ''}
            />
          </View.Item>
        )}
        <View.Item gapBefore={{ s: 2, m: 3, l: 2, xl: 2 }}>
          <View direction="row">
            <View.Item>{pdpPartNumber()}</View.Item>
            <View.Item gapBefore={5}>{pdpRatings()}</View.Item>
          </View>
        </View.Item>
        <View.Item gapBefore={2}>{pdpWarranty()}</View.Item>
        {showFitmentBadge && <View.Item gapBefore={4}>{renderFitmentBadge()}</View.Item>}
        {isSuperSkusEnabled && !!productDetails?.availableOptions && !!productDetails?.choices && (
          <View.Item>
            <View direction="column">
              {isDesktop && horizontalRule(5)}
              {isSuperSkusEnabled &&
                !!productDetails?.availableOptions &&
                !!productDetails?.choices && (
                  <View.Item>
                    <View direction="column" className={styles.superSkuOptionsContainerDesktop}>
                      {[...optionsWithoutImages, ...optionsWithImages].map((option, index) => (
                        <View.Item key={`${option}-${index}`}>
                          <SuperSkuOptionSelector
                            optionName={option}
                            availableOptions={productDetails.availableOptions}
                            choices={productDetails.choices}
                            currentSelection={currentSuperSkuSelection}
                            shouldShowSkeleton={shouldShowSkeleton}
                          />
                        </View.Item>
                      ))}
                    </View>
                  </View.Item>
                )}
            </View>
          </View.Item>
        )}
        {pdpHasSpecifications && (
          <View.Item gapBefore={{ s: 4, m: 4, l: 0 }}>
            {isDesktop ? horizontalRule(5) : horizontalRule(4)}
            <PdpSpecifications />
          </View.Item>
        )}
        {productCompareDescriptions?.[productCompareLocaleMap as 'EN_US' | 'ES_MX' | 'PT_BR'] && (
          <View.Item>
            {horizontalRule(4)}
            <PdpFeatures productCompareDescriptions={productCompareDescriptions} />
          </View.Item>
        )}
        {props.hasVehicle && !!pdpTechNotes && (
          <View.Item>
            {horizontalRule(4)}
            {pdpTechNotes}
          </View.Item>
        )}
      </View>
    );
  };

  type ResponsivePadding = number | [number, number];
  type ResponsiveParam = {
    s?: ResponsivePadding;
    m?: ResponsivePadding;
    l?: ResponsivePadding;
    xl?: ResponsivePadding;
  };

  const horizontalRule = (padding: number | ResponsiveParam) => {
    let top: number, bottom: number;

    if (typeof padding === 'number') {
      top = bottom = padding;
    } else {
      const getValue = (): ResponsivePadding => {
        if (isMobile) {
          return padding.s ?? padding.m ?? padding.l ?? padding.xl ?? 0;
        }
        if (isTablet) {
          return padding.m ?? padding.s ?? padding.l ?? padding.xl ?? 0;
        }
        if (isDesktopXL) {
          return padding.xl ?? padding.l ?? padding.m ?? padding.s ?? 0;
        }
        if (isDesktop) {
          return padding.l ?? padding.xl ?? padding.m ?? padding.s ?? 0;
        }
        return 0;
      };

      const value = getValue();
      [top, bottom] = Array.isArray(value) ? value : [value, value];
    }

    return (
      <View padding={[top, 0, bottom, 0]}>
        <View className={styles.hr} />
      </View>
    );
  };

  /**
   * Right column content for desktop 3-column layout
   * Contains: Price, quantity, deals, rewards, fulfillment options, cart button
   */
  const renderRightColumn = () => {
    return (
      <View
        direction="column"
        padding={{ s: 4, m: 4, l: 3 }}
        attributes={{ style: { backgroundColor: '#f8f8f8', borderRadius: '4px' } }}
      >
        {!isDesktop && <View className={styles.hr} />}
        {pdpPriceDisplay()}
        {pdpFulfillmentOptionsBlock()}
        {horizontalRule(6)}
        <View direction="row" gap={3}>
          <View.Item>{pdpQuantityStepper()}</View.Item>
          <View.Item grow>{pdpAddToCartCTA()}</View.Item>
        </View>
        {pdpDealsButton()}
        {displayDeals()}
      </View>
    );
  };

  /**
   * Single column layout for mobile/tablet
   * Contains all PDP sections in mobile-optimized order
   */
  const pdpSingleColumn = () => {
    const productTitle = productDetails?.itemDescription ?? '';
    const productBrand = productDetails?.brandName ?? '';
    const brandURL = productDetails?.brandUrl ?? '';

    return (
      <View
        direction="column"
        attributes={{ style: { display: 'flex', flexGrow: 1, minWidth: 0 } }}
      >
        {shouldShowSkeleton ? (
          <MiddleColumnSkeletonTop />
        ) : (
          <>
            <View.Item>
              <View padding={{ s: [0, 4], m: 0 }}>
                {previouslyBoughtItem?.transactionDate ? (
                  <View padding={{ s: [1, 0, 2, 0], m: [2, 0] }}>
                    <PurchasedDate purchasedDate={previouslyBoughtItem.transactionDate} />
                  </View>
                ) : null}
                <PdpTitleNotesV2
                  productTitle={productTitle}
                  productBrand={productBrand}
                  brandURL={brandURL}
                  shouldShowSkeleton={shouldShowSkeleton}
                  productDetails={productDetails}
                />
              </View>
            </View.Item>
            {!!oemPartNumber && (
              <View.Item gapBefore={2}>
                <View direction="row" padding={{ s: [0, 4], m: 0 }}>
                  <ProductCrossReference
                    oemBrandName={oemBrandName || ''}
                    oemPartNumber={oemPartNumber || ''}
                  />
                </View>
              </View.Item>
            )}
            <View.Item gapBefore={{ s: 2, m: 3, l: 2, xl: 2 }}>
              <View direction="row" padding={{ s: [0, 4], m: 0 }}>
                <View.Item>{pdpPartNumber()}</View.Item>
                <View.Item gapBefore={5}>{pdpRatings()}</View.Item>
              </View>
            </View.Item>
            <View.Item gapBefore={{ s: 2, m: 3 }}>
              <View padding={{ s: [0, 4], m: 0 }}>{pdpWarranty()}</View>
            </View.Item>
            {showFitmentBadge && (
              <View.Item gapBefore={{ s: 5, m: 4 }}>
                <View padding={{ s: [0, 4], m: 0 }}>{renderFitmentBadge()}</View>
              </View.Item>
            )}
          </>
        )}
        <View.Item gapBefore={8}>
          <View padding={{ s: [0, 4], m: 0 }}>
            {shouldShowSkeleton ? (
              <LeftSectionSkeletonLoading />
            ) : (
              <ProductCarouselV2 data={productDetails!} labels={labels} />
            )}
          </View>
        </View.Item>
        {shouldDisplaySuperSkus && (
          <View.Item>
            <View padding={{ s: [5, 4], m: [5, 0] }}>
              {isMobile && horizontalRule({ s: [0, 4] })}
              {pdpSuperSkuSelector()}
            </View>
          </View.Item>
        )}
        <View.Item gapBefore={5}>
          <View
            padding={{ s: [4, 0], m: 4 }}
            attributes={{ style: { backgroundColor: '#f8f8f8' } }}
          >
            <View.Item>
              <View padding={{ s: [0, 4], m: 0 }}>{pdpPriceDisplay()}</View>
            </View.Item>
            <View.Item>
              <View padding={{ s: [0, 4], m: 0 }}>{pdpFulfillmentOptionsBlock()}</View>
            </View.Item>
            <View.Item>
              <View padding={{ s: [0, 4], m: 0 }}>
                {horizontalRule(6)}
                <View direction="row" gap={3}>
                  <View.Item>{pdpQuantityStepper()}</View.Item>
                  <View.Item grow>{pdpAddToCartCTA()}</View.Item>
                </View>
              </View>
            </View.Item>
          </View>
        </View.Item>
        {shouldShowSkeleton ? (
          <MiddleColumnSkeletonBottom />
        ) : (
          <>
            {pdpHasSpecifications && (
              <View.Item gapBefore={{ l: 5 }}>
                <View padding={{ s: [5, 4, 0, 4], m: [5, 0, 0, 0] }}>
                  <View padding={{ l: [4, 0] }}>
                    <PdpSpecifications />
                  </View>
                </View>
              </View.Item>
            )}
            {productCompareDescriptions?.[
              productCompareLocaleMap as 'EN_US' | 'ES_MX' | 'PT_BR'
            ] && (
              <View.Item>
                <View padding={{ s: [0, 4], m: 0 }}>
                  {horizontalRule({ s: 5, m: 4 })}
                  <View padding={[4, 0]}>
                    <PdpFeatures productCompareDescriptions={productCompareDescriptions} />
                  </View>
                </View>
              </View.Item>
            )}
            {props.hasVehicle && !!pdpTechNotes && (
              <View.Item>
                <View padding={{ s: [0, 4], m: 0 }}>
                  {horizontalRule({ s: 5, m: 4 })}
                  {pdpTechNotes}
                </View>
              </View.Item>
            )}
          </>
        )}
      </View>
    );
  };
  if (!skuDetailsData && !shouldShowSkeleton) {
    return null;
  }

  return (
    <View attributes={{ style: { display: 'flex', flexGrow: '1', minWidth: 0 } }}>
      {isDesktop || isDesktopXL ? (
        <ProductAvailabilityInfoSkeletonV2
          isLoading={shouldShowSkeleton}
          leftColumn={shouldShowSkeleton ? <LeftSectionSkeletonLoading /> : renderLeftColumn()}
          middleColumn={shouldShowSkeleton ? <MiddleColumnSkeleton /> : renderMiddleColumn()}
          rightColumn={renderRightColumn()}
        />
      ) : (
        pdpSingleColumn()
      )}
      <YMMEModal
        showYmme={showYmme}
        closeYmmeWidget={() => setShowYmme(false)}
        pageType={pdpCheckFits}
        onUpdateVehicleSuccess={() => setShowYmme(false)}
        partGroupId={productDetails?.partGroupId}
        partGroupName={productDetails?.partTypeName}
      />
    </View>
  );
}
